import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Plus, Trash2, Search, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, BarChart3, Target, Calendar, Award, Flame, X } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSIONS = ['New York', 'London', 'Tokyo', 'Sydney'];
const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const DIRECTIONS = ['Compra', 'Venta'];
const STATUSES = ['Pendiente', 'Take Profit', 'Stop Loss', 'Break Even', 'Parcial'];
const TRADE_TYPES = ['Scalping', 'Day Trade', 'Swing', 'Intradía'];

const statusColors = {
  'Pendiente': 'bg-gray-500/30 text-gray-300',
  'Take Profit': 'bg-green-500/30 text-green-400',
  'Stop Loss': 'bg-red-500/30 text-red-400',
  'Break Even': 'bg-yellow-500/30 text-yellow-400',
  'Parcial': 'bg-blue-500/30 text-blue-400',
};
const dirColors = { 'Compra': 'bg-green-600/40 text-green-300', 'Venta': 'bg-red-600/40 text-red-300' };
const sessionColors = { 'New York': 'bg-blue-600/40 text-blue-300', 'London': 'bg-purple-600/40 text-purple-300', 'Tokyo': 'bg-orange-600/40 text-orange-300', 'Sydney': 'bg-teal-600/40 text-teal-300' };

function AutoInput({ value, onChange, suggestions, placeholder, className }) {
  const [show, setShow] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    if (value && suggestions.length) setFiltered(suggestions.filter(s => s.toLowerCase().includes(value.toLowerCase()) && s !== value));
    else setFiltered([]);
  }, [value, suggestions]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return (
    <div ref={ref} className="relative">
      <input value={value} onChange={(e) => { onChange(e.target.value); setShow(true); }} onFocus={() => setShow(true)} placeholder={placeholder} className={className} />
      {show && filtered.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 bg-[#0a140a] border border-emerald-500/30 rounded-b-md max-h-32 overflow-y-auto">
          {filtered.map((s, i) => <div key={i} onClick={() => { onChange(s); setShow(false); }} className="px-2 py-1.5 text-xs text-emerald-100 hover:bg-emerald-500/20 cursor-pointer">{s}</div>)}
        </div>
      )}
    </div>
  );
}

function SelectCell({ value, onChange, options, colorMap }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`w-full bg-transparent text-xs px-1 py-1 rounded border-0 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 cursor-pointer ${(colorMap && colorMap[value]) || 'text-emerald-100'}`}>
      <option value="" className="bg-[#050a05]">-</option>
      {options.map(o => <option key={o} value={o} className="bg-[#050a05]">{o}</option>)}
    </select>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'emerald' }) {
  const colors = {
    emerald: 'border-emerald-500/20 text-emerald-400',
    green: 'border-green-500/20 text-green-400',
    red: 'border-red-500/20 text-red-400',
    blue: 'border-blue-500/20 text-blue-400',
    yellow: 'border-yellow-500/20 text-yellow-400',
    purple: 'border-purple-500/20 text-purple-400',
  };
  return (
    <div className={`bg-black/30 border ${colors[color]?.split(' ')[0] || 'border-emerald-500/20'} rounded-lg p-3`}>
      <div className="flex items-center space-x-2 mb-1">
        <Icon className={`h-3.5 w-3.5 ${colors[color]?.split(' ')[1] || 'text-emerald-400'} opacity-60`} />
        <span className="text-emerald-100/40 text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg font-bold ${colors[color]?.split(' ')[1] || 'text-emerald-400'}`} style={{ fontFamily: 'Rajdhani, sans-serif' }}>{value}</p>
      {sub && <p className="text-emerald-100/30 text-[10px]">{sub}</p>}
    </div>
  );
}

const METALS = ['XAUUSD', 'XAGUSD', 'GOLD', 'SILVER', 'XAU', 'XAG'];
const CFDS = ['NAS100', 'US30', 'US500', 'SPX', 'DAX', 'FTSE', 'NAS', 'NDX', 'DJI', 'DOW', 'BTC', 'ETH', 'SOL'];

function getAssetClass(pair) {
  if (!pair) return 'forex';
  const clean = pair.toUpperCase().replace(/[/ ]/g, '');
  if (METALS.some(m => clean.includes(m))) return 'metales';
  if (CFDS.some(c => clean.includes(c))) return 'cfds';
  return 'forex';
}

function parseRRR(rrr) {
  if (!rrr) return null;
  const parts = rrr.toString().split(':');
  if (parts.length === 2) return parseFloat(parts[1]) / parseFloat(parts[0]);
  const val = parseFloat(rrr);
  return isNaN(val) ? null : val;
}

function getTradeR(entry) {
  // Returns the R value: +R for wins, -1R for losses based on RRR
  const rrr = parseRRR(entry.rrr);
  if (entry.status === 'Take Profit') return rrr || 1;
  if (entry.status === 'Stop Loss') return -1;
  if (entry.status === 'Break Even') return 0;
  if (entry.status === 'Parcial') return rrr ? rrr * 0.5 : 0.5;
  return 0;
}

function StatsPanel({ entries }) {
  const stats = useMemo(() => {
    const closed = entries.filter(e => e.status && e.status !== 'Pendiente');
    const wins = closed.filter(e => e.status === 'Take Profit');
    const losses = closed.filter(e => e.status === 'Stop Loss');
    const totalTrades = closed.length;
    const winRate = totalTrades > 0 ? Math.round((wins.length / totalTrades) * 100) : 0;

    // Total R
    const totalR = closed.reduce((acc, e) => acc + getTradeR(e), 0);

    // Most traded pair
    const pairCounts = {};
    entries.forEach(e => { if (e.pair) pairCounts[e.pair] = (pairCounts[e.pair] || 0) + 1; });
    const topPair = Object.entries(pairCounts).sort((a, b) => b[1] - a[1])[0];

    // Most profitable pair (by R)
    const pairR = {};
    closed.forEach(e => { if (e.pair) pairR[e.pair] = (pairR[e.pair] || 0) + getTradeR(e); });
    const bestPair = Object.entries(pairR).sort((a, b) => b[1] - a[1])[0];

    // Best day of week (by R)
    const dayR = {};
    closed.forEach(e => { if (e.day) dayR[e.day] = (dayR[e.day] || 0) + getTradeR(e); });
    const bestDay = Object.entries(dayR).sort((a, b) => b[1] - a[1])[0];

    // Best session (by R)
    const sessR = {};
    closed.forEach(e => { if (e.session) sessR[e.session] = (sessR[e.session] || 0) + getTradeR(e); });
    const bestSession = Object.entries(sessR).sort((a, b) => b[1] - a[1])[0];

    // Biggest single win (by R)
    const biggestWin = closed.reduce((max, e) => getTradeR(e) > getTradeR(max || {}) ? e : max, null);
    const biggestR = biggestWin ? getTradeR(biggestWin) : 0;

    // Average RRR
    const rrrValues = closed.filter(e => e.rrr).map(e => parseRRR(e.rrr)).filter(v => v !== null);
    const avgRRR = rrrValues.length > 0 ? (rrrValues.reduce((a, b) => a + b, 0) / rrrValues.length).toFixed(1) : '—';

    return {
      totalTrades, winRate, totalR: +totalR.toFixed(1),
      wins: wins.length, losses: losses.length,
      topPair: topPair ? topPair[0] : '—', topPairCount: topPair ? topPair[1] : 0,
      bestPair: bestPair ? bestPair[0] : '—', bestPairR: bestPair ? +bestPair[1].toFixed(1) : 0,
      bestDay: bestDay ? bestDay[0] : '—', bestDayR: bestDay ? +bestDay[1].toFixed(1) : 0,
      bestSession: bestSession ? bestSession[0] : '—', bestSessionR: bestSession ? +bestSession[1].toFixed(1) : 0,
      biggestR: +biggestR.toFixed(1), biggestWinPair: biggestWin?.pair || '—',
      avgRRR,
    };
  }, [entries]);

  if (entries.length === 0) return null;

  const fmtR = (v) => v > 0 ? `+${v}R` : `${v}R`;

  return (
    <div className="glass-panel rounded-lg p-4 mb-4" data-testid="journal-stats">
      <h3 className="text-sm font-bold text-emerald-50 mb-3 flex items-center space-x-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
        <BarChart3 className="h-4 w-4 text-emerald-400" />
        <span>ESTADÍSTICAS DE TRADING</span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        <StatCard icon={Target} label="Win Rate" value={`${stats.winRate}%`} sub={`${stats.wins}W / ${stats.losses}L de ${stats.totalTrades}`} color={stats.winRate >= 50 ? 'green' : 'red'} />
        <StatCard icon={TrendingUp} label="Rendimiento Total" value={fmtR(stats.totalR)} color={stats.totalR >= 0 ? 'green' : 'red'} />
        <StatCard icon={BarChart3} label="Par Más Operado" value={stats.topPair} sub={`${stats.topPairCount} operaciones`} color="blue" />
        <StatCard icon={Award} label="Par Más Rentable" value={stats.bestPair} sub={stats.bestPairR !== 0 ? fmtR(stats.bestPairR) : ''} color="green" />
        <StatCard icon={Calendar} label="Mejor Día" value={stats.bestDay} sub={stats.bestDayR !== 0 ? fmtR(stats.bestDayR) : ''} color="purple" />
        <StatCard icon={TrendingUp} label="Mejor Sesión" value={stats.bestSession} sub={stats.bestSessionR !== 0 ? fmtR(stats.bestSessionR) : ''} color="blue" />
        <StatCard icon={Award} label="Mayor Ganancia" value={fmtR(stats.biggestR)} sub={stats.biggestWinPair} color={stats.biggestR > 0 ? 'green' : 'yellow'} />
        <StatCard icon={Target} label="RRR Promedio" value={`1:${stats.avgRRR}`} color="emerald" />
      </div>
    </div>
  );
}

function PerformanceChart({ entries }) {
  const CLASSES = [
    { key: 'forex', label: 'FOREX', color: '#10B981', colorNeg: '#EF4444' },
    { key: 'metales', label: 'METALES', color: '#F59E0B', colorNeg: '#EF4444' },
    { key: 'cfds', label: 'CFDs / ÍNDICES', color: '#3B82F6', colorNeg: '#EF4444' },
  ];

  const chartsByClass = useMemo(() => {
    const result = {};
    CLASSES.forEach(cls => {
      const closed = entries
        .filter(e => e.status && e.status !== 'Pendiente' && e.date && getAssetClass(e.pair) === cls.key)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      if (closed.length < 2) { result[cls.key] = []; return; }
      
      let cumR = 0;
      result[cls.key] = closed.map((e) => {
        cumR += getTradeR(e);
        return {
          date: e.date,
          label: `${e.date?.slice(5)}`,
          r: +cumR.toFixed(1),
          trade: `${e.pair || '?'} ${e.direction === 'Compra' ? 'L' : 'S'} ${getTradeR(e) > 0 ? '+' : ''}${getTradeR(e).toFixed(1)}R`,
        };
      });
    });
    return result;
  }, [entries]);

  const hasAny = CLASSES.some(cls => chartsByClass[cls.key]?.length >= 2);
  if (!hasAny) return null;

  return (
    <div className="space-y-4 mb-4">
      {CLASSES.map(cls => {
        const data = chartsByClass[cls.key];
        if (!data || data.length < 2) return null;
        const isPositive = data[data.length - 1]?.r >= 0;
        const gradId = `rrrGrad_${cls.key}`;
        return (
          <div key={cls.key} className="glass-panel rounded-lg p-4" data-testid={`chart-${cls.key}`}>
            <h3 className="text-sm font-bold text-emerald-50 mb-3 flex items-center space-x-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              <TrendingUp className="h-4 w-4" style={{ color: cls.color }} />
              <span>RENDIMIENTO {cls.label} (RRR)</span>
              <span className={`text-xs font-mono ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {data[data.length - 1]?.r > 0 ? '+' : ''}{data[data.length - 1]?.r}R
              </span>
            </h3>
            <div className="h-44 sm:h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={isPositive ? cls.color : cls.colorNeg} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={isPositive ? cls.color : cls.colorNeg} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(16,185,129,0.08)" />
                  <XAxis dataKey="label" tick={{ fill: 'rgba(236,253,245,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(236,253,245,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `${v}R`} />
                  <ReferenceLine y={0} stroke="rgba(236,253,245,0.15)" strokeDasharray="3 3" />
                  <Tooltip
                    contentStyle={{ background: '#0a140a', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#6EE7B7' }}
                    itemStyle={{ color: '#ECFDF5' }}
                    formatter={(val, name, props) => [`${val}R`, props.payload.trade]}
                  />
                  <Area type="monotone" dataKey="r" stroke={isPositive ? cls.color : cls.colorNeg} strokeWidth={2} fill={`url(#${gradId})`} dot={false} activeDot={{ r: 4, fill: isPositive ? cls.color : cls.colorNeg }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StreakBadge({ entries }) {
  const streak = useMemo(() => {
    const closed = entries
      .filter(e => e.status && e.status !== 'Pendiente' && e.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // newest first
    
    if (closed.length === 0) return { count: 0, type: null };
    
    const firstStatus = closed[0].status;
    const isWin = firstStatus === 'Take Profit';
    const isLoss = firstStatus === 'Stop Loss';
    if (!isWin && !isLoss) return { count: 0, type: null };
    
    let count = 0;
    for (const e of closed) {
      if (e.status === firstStatus) count++;
      else break;
    }
    
    return { count, type: isWin ? 'win' : 'loss' };
  }, [entries]);

  if (streak.count < 3) return null;

  const isWin = streak.type === 'win';

  return (
    <div className={`glass-panel rounded-lg p-3 mb-4 flex items-center space-x-3 ${isWin ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}`} data-testid="streak-badge">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isWin ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
        <Flame className={`h-5 w-5 ${isWin ? 'text-green-400' : 'text-red-400'}`} />
      </div>
      <div>
        <p className={`font-bold text-sm ${isWin ? 'text-green-400' : 'text-red-400'}`} style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          {isWin ? `RACHA GANADORA x${streak.count}` : `RACHA PERDEDORA x${streak.count}`}
        </p>
        <p className="text-emerald-100/40 text-xs">
          {isWin
            ? streak.count >= 5 ? 'Imparable. Sigue así.' : 'Estás en racha. No bajes la guardia.'
            : streak.count >= 5 ? 'Detente. Revisa tu estrategia antes de continuar.' : 'Analiza tus últimas operaciones antes de seguir.'
          }
        </p>
      </div>
    </div>
  );
}

export default function TradeJournal() {
  const [entries, setEntries] = useState([]);
  const [autocomplete, setAutocomplete] = useState({ pairs: [], trade_types: [], sessions: [] });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [ent, auto] = await Promise.all([axios.get(`${API}/journal`), axios.get(`${API}/journal/autocomplete`)]);
      setEntries(ent.data);
      setAutocomplete(auto.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addRow = async () => {
    try {
      const res = await axios.post(`${API}/journal`, { status: 'Pendiente' });
      setEntries([res.data, ...entries]);
      setPage(1);
    } catch { toast.error('Error'); }
  };

  const updateField = async (id, field, value) => {
    const entry = entries.find(e => e.id === id);
    if (!entry) return;
    const updated = { ...entry, [field]: value };
    setEntries(entries.map(e => e.id === id ? updated : e));
    try {
      await axios.put(`${API}/journal/${id}`, updated);
      if (['pair', 'trade_type', 'session'].includes(field)) {
        const auto = await axios.get(`${API}/journal/autocomplete`);
        setAutocomplete(auto.data);
      }
    } catch {}
  };

  const deleteRow = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`);
      setEntries(entries.filter(e => e.id !== id));
    } catch {}
  };

  const filtered = entries.filter(e => {
    if (filter !== 'all' && e.status !== filter) return false;
    if (search && !`${e.pair}${e.trade_type}${e.session}${e.direction}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && e.date && e.date < dateFrom) return false;
    if (dateTo && e.date && e.date > dateTo) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / perPage);
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [filter, search, perPage, dateFrom, dateTo]);

  const allPairs = [...new Set([...autocomplete.pairs, 'XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY', 'NAS100', 'US30', 'BTC/USD'])];

  return (
    <div data-testid="trade-journal">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>BITÁCORA OPERATIVA</h2>
          <p className="text-emerald-100/50 text-sm">Seguimiento de tus operaciones</p>
        </div>
        <button onClick={addRow} className="flex items-center space-x-2 bg-emerald-500 text-black px-4 py-2 rounded-lg hover:bg-emerald-400 font-bold text-sm" data-testid="add-entry-btn">
          <Plus className="h-4 w-4" /><span>Nueva Entrada</span>
        </button>
      </div>

      {/* Stats Dashboard */}
      <StatsPanel entries={entries} />

      {/* Streak Badge */}
      <StreakBadge entries={entries} />

      {/* Performance Chart */}
      <PerformanceChart entries={entries} />

      {/* Filters + Per Page */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center bg-black/30 border border-emerald-500/20 rounded-lg px-3 py-1.5">
          <Search className="h-3.5 w-3.5 text-emerald-100/40 mr-2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="bg-transparent text-sm text-emerald-50 placeholder:text-emerald-100/30 focus:outline-none w-32" data-testid="journal-search" />
        </div>
        {['all', 'Pendiente', 'Take Profit', 'Stop Loss', 'Break Even'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1.5 rounded-lg transition-all ${filter === f ? 'bg-emerald-500 text-black font-bold' : 'bg-black/30 border border-emerald-500/20 text-emerald-100/70 hover:bg-emerald-500/10'}`} data-testid={`filter-${f}`}>
            {f === 'all' ? 'Todas' : f}
          </button>
        ))}
      </div>

      {/* Date Filter + Per Page */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center space-x-1.5 bg-black/30 border border-emerald-500/20 rounded-lg px-2.5 py-1">
          <Calendar className="h-3.5 w-3.5 text-emerald-100/40" />
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-transparent text-xs text-emerald-100 focus:outline-none" data-testid="date-from" />
          <span className="text-emerald-100/30 text-xs">a</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-transparent text-xs text-emerald-100 focus:outline-none" data-testid="date-to" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-emerald-100/30 hover:text-red-400 ml-1" data-testid="date-clear">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="ml-auto flex items-center space-x-2">
          <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="bg-black/30 border border-emerald-500/20 rounded-lg text-emerald-100/70 text-xs px-2 py-1.5 focus:outline-none" data-testid="per-page-select">
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
          </select>
          <span className="text-emerald-100/40 text-xs">{filtered.length} operaciones</span>
        </div>
      </div>

      {/* Table */}
      <div className="glass-panel rounded-lg overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-emerald-500/20 text-left">
              {['Tipo', 'Fecha', 'Sesión', 'Par', 'Día', 'Entrada', 'Estado', 'RRR', 'Pips SL', 'MAE', 'Orden', ''].map(h => (
                <th key={h} className="px-2 py-2.5 text-[10px] text-emerald-100/50 font-bold uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((e) => (
              <tr key={e.id} className="border-b border-emerald-500/10 hover:bg-emerald-500/5 group" data-testid={`journal-row-${e.id}`}>
                <td className="px-2 py-1"><SelectCell value={e.trade_type || ''} onChange={(v) => updateField(e.id, 'trade_type', v)} options={[...new Set([...TRADE_TYPES, ...autocomplete.trade_types])]} /></td>
                <td className="px-2 py-1"><input type="date" value={e.date || ''} onChange={(ev) => updateField(e.id, 'date', ev.target.value)} className="bg-transparent text-xs text-emerald-100 focus:outline-none w-28" /></td>
                <td className="px-2 py-1"><SelectCell value={e.session || ''} onChange={(v) => updateField(e.id, 'session', v)} options={[...new Set([...SESSIONS, ...autocomplete.sessions])]} colorMap={sessionColors} /></td>
                <td className="px-2 py-1 w-24"><AutoInput value={e.pair || ''} onChange={(v) => updateField(e.id, 'pair', v)} suggestions={allPairs} placeholder="Par" className="bg-transparent text-xs text-emerald-50 focus:outline-none w-full px-1 py-1 rounded focus:ring-1 focus:ring-emerald-500/50" /></td>
                <td className="px-2 py-1"><SelectCell value={e.day || ''} onChange={(v) => updateField(e.id, 'day', v)} options={DAYS} /></td>
                <td className="px-2 py-1"><SelectCell value={e.direction || ''} onChange={(v) => updateField(e.id, 'direction', v)} options={DIRECTIONS} colorMap={dirColors} /></td>
                <td className="px-2 py-1"><SelectCell value={e.status || 'Pendiente'} onChange={(v) => updateField(e.id, 'status', v)} options={STATUSES} colorMap={statusColors} /></td>
                <td className="px-2 py-1"><input value={e.rrr || ''} onChange={(ev) => updateField(e.id, 'rrr', ev.target.value)} placeholder="1:2" className="bg-transparent text-xs text-emerald-100 focus:outline-none w-12 px-1 py-1 rounded focus:ring-1 focus:ring-emerald-500/50" /></td>
                <td className="px-2 py-1"><input type="number" value={e.pips_sl ?? ''} onChange={(ev) => updateField(e.id, 'pips_sl', ev.target.value ? parseFloat(ev.target.value) : null)} className={`bg-transparent text-xs focus:outline-none w-16 px-1 py-1 rounded focus:ring-1 focus:ring-emerald-500/50 ${(e.pips_sl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} /></td>
                <td className="px-2 py-1"><input type="number" value={e.mae ?? ''} onChange={(ev) => updateField(e.id, 'mae', ev.target.value ? parseFloat(ev.target.value) : null)} className={`bg-transparent text-xs focus:outline-none w-16 px-1 py-1 rounded focus:ring-1 focus:ring-emerald-500/50 ${(e.mae || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} /></td>
                <td className="px-2 py-1"><input value={e.order_type || ''} onChange={(ev) => updateField(e.id, 'order_type', ev.target.value)} placeholder="Operativa" className="bg-transparent text-xs text-emerald-100 focus:outline-none w-20 px-1 py-1 rounded focus:ring-1 focus:ring-emerald-500/50" /></td>
                <td className="px-1 py-1"><button onClick={() => deleteRow(e.id)} className="text-emerald-100/20 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"><Trash2 className="h-3 w-3" /></button></td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-emerald-100/30 text-sm">No hay operaciones registradas. Haz clic en "Nueva Entrada" para comenzar.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 px-1" data-testid="journal-pagination">
          <p className="text-emerald-100/40 text-xs">
            Mostrando {(page - 1) * perPage + 1}-{Math.min(page * perPage, filtered.length)} de {filtered.length}
          </p>
          <div className="flex items-center space-x-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-lg bg-black/30 border border-emerald-500/20 text-emerald-100/60 hover:bg-emerald-500/10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="page-prev">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)} className={`min-w-[28px] h-7 text-xs rounded-lg transition-all ${p === page ? 'bg-emerald-500 text-black font-bold' : 'bg-black/30 border border-emerald-500/20 text-emerald-100/60 hover:bg-emerald-500/10'}`} data-testid={`page-${p}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg bg-black/30 border border-emerald-500/20 text-emerald-100/60 hover:bg-emerald-500/10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="page-next">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
